import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useExpertStripeOnboardingLink } from '~/modules/connect/useExpertStripeOnboardingLink';

/**
 * Stripe Account Link `refresh_url` when the previous link expired—request a new link and redirect.
 */
const ExpertStripeOnboardingRefreshPage = () => {
  const { getOnboardingUrl } = useExpertStripeOnboardingLink();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const requestFreshOnboardingLinkAndRedirect = async () => {
      try {
        const url = await getOnboardingUrl();
        if (cancelled) {
          return;
        }
        if (url) {
          window.location.assign(url);
          return;
        }
        setErrorMessage('Could not start Stripe onboarding. Please try again.');
      } catch (error) {
        if (cancelled) {
          return;
        }
        Sentry.captureException(error, {
          tags: { feature: 'stripe_connect_refresh' },
        });
        const message =
          error instanceof Error
            ? error.message
            : 'Could not start Stripe onboarding. Please try again.';
        setErrorMessage(message);
      }
    };

    void requestFreshOnboardingLinkAndRedirect();

    return () => {
      cancelled = true;
    };
  }, [getOnboardingUrl]);

  return (
    <Root spacing={2}>
      <PageTitle variant="h6">Continue payout setup</PageTitle>
      {!errorMessage && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={22} aria-hidden />
          <Body variant="body2" color="text.secondary">
            Redirecting to Stripe…
          </Body>
        </Stack>
      )}
      {errorMessage && (
        <>
          <Body variant="body2" color="error">
            {errorMessage}
          </Body>
          <Button
            component={RouterLink}
            to="/connect/setup-payouts"
            variant="outlined"
            fullWidth
          >
            Back to payout setup
          </Button>
        </>
      )}
    </Root>
  );
};

const Root = styled(Stack)(({ theme }) => ({
  maxWidth: 480,
  marginLeft: 'auto',
  marginRight: 'auto',
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
}));

const Body = styled(Typography)(() => ({}));

export default ExpertStripeOnboardingRefreshPage;
